import {
  IonButton,
  IonContent,
  IonIcon,
  IonPage,
  IonSegment,
  IonSegmentButton,
  useIonModal
} from "@ionic/react";
import BotHeader from "../components/BotHeader";
import { useParams } from "react-router-dom";
import { modeMap, useBotsStore } from "../stores/bots.store";
import Header from "../components/Header";
import GradientLineChart from "../components/GradientLineChart";
import moment from "moment";
import { useState } from "react";
import TradeList from "../components/TradeList";
import CoinList from "../components/CoinList";
import {
  addOutline,
  refreshOutline,
  removeCircleOutline
} from "ionicons/icons";
import GenericInfo from "./GenericInfo";
import { addBotAction, createActionByType } from "../lib/firebase";
import { useAuthStore } from "../stores/auth.store";
import AnimationContainer from "../components/Animation";
import ConfigSummary from "../components/ConfigSummary";
import { useSubscribeToBotData } from "../hooks/useSubscribeToBotData";
import HelpButton from "../components/HelpButton";

const Bot: React.FC = () => {
  const uid = useAuthStore(state => state.user?.uid);
  const botId = useParams<{ botId: string }>().botId;
  const bot = useBotsStore(state => state.bots.find(b => b.id === botId));
  const [activeSegment, setActiveSegment] = useState(
    bot?.active ? "portfolio" : "trades"
  );
  useSubscribeToBotData(botId);

  const [activeAction, setActiveAction] = useState<string | null>();
  const [presentConfirm, dismissConfirm] = useIonModal(GenericInfo, {
    onDismiss: (confirm: boolean) => {
      dismissConfirm();
      if (confirm && uid && botId && activeAction) {
        const action = createActionByType(activeAction, uid, {
          botId
        });
        addBotAction(botId, action)
          .then(() => console.log(`${activeAction} signal sent.`))
          .catch((error: any) =>
            console.error("Data could not be saved." + error)
          );
      }
    },
    title: activeAction === "resetPortfolio" ? "Reset Portfolio" : "Clear Dust",
    subtitle:
      activeAction === "resetPortfolio"
        ? "Are you sure you want to reset your portfolio? This will close all your open trades and convert all to USDT."
        : "Are you sure you want to clear your dust? This will convert all small balances to BNB.",
    buttonText:
      activeAction === "resetPortfolio"
        ? "Yes - Reset Portfolio"
        : "Yes - Clear Dust",
    icon:
      activeAction === "resetPortfolio" ? refreshOutline : removeCircleOutline
  });

  if (!botId || !bot) {
    return null;
  }

  const formattedData: [string, number][] =
    bot?.data?.portfolios &&
    Object.entries(bot.data.portfolios).map(([key, value]) => {
      // @ts-ignore
      const date = moment(value.timestamp * 1000).format("YYYY-MM-DD HH:mm:ss");
      // @ts-ignore
      const totalValue = value.totalValue;
      return [date, totalValue];
    });

  const segments = [
    { value: "portfolio", text: "Portfolio", disabled: false },
    { value: "trades", text: "Trades", disabled: false },
    { value: "options", text: "Buy Options", disabled: false },
    { value: "config", text: "Config", disabled: false }
  ];

  if (!bot.active || bot.mode === "backtest" || bot.user !== uid) {
    // remove chart segment
    segments.splice(2, 1);
  }

  return (
    <IonPage>
      <Header title={`${modeMap[bot.mode]} Bots`} back={true} />
      {/* scrollY={false} */}
      <IonContent fullscreen>
        <div className="w-screen mx-auto">
          <BotHeader botId={botId} />
        </div>
        {!bot?.provisioned && (
          <div>
            <AnimationContainer animationName="armAnimation" />
            <div className="text-2xl font-semibold text-primary-50 text-center">
              Bot Scheduled
            </div>
          </div>
        )}
        {bot?.provisioned && (
          <>
            <IonSegment
              scrollable
              className="bg-black -my-4 scrollable space-x-4 overflow-x-auto scrollbar-hide justify-center"
              value={activeSegment}
              onIonChange={event =>
                setActiveSegment(event.detail.value || "portfolio")
              }
            >
              {segments.map(segment => (
                <IonSegmentButton
                  key={segment.value}
                  mode="md"
                  className={`text-[10px] bg-transparent whitespace-nowrap ${
                    activeSegment === segment.value
                      ? "border-b-4 border-primary-50"
                      : "text-primary-50"
                  }`}
                  value={segment.value}
                  onClick={() => setActiveSegment(segment.value)}
                  disabled={segment.disabled}
                >
                  {segment.text}
                </IonSegmentButton>
              ))}
            </IonSegment>
            {/* style={{ overflow: "auto", maxHeight: "calc(100vh - 450px)" }} */}
            <div className="max-w-xl mx-auto pt-8 ">
              {activeSegment === "portfolio" && (
                <div className="bg-primary-90">
                  <div className="flex justify-between items-center px-4">
                    <div className="text-2xl text-white font-semibold flex items-center">
                      Portfolio
                      <HelpButton id="bot_portfolio" />
                    </div>
                    {bot?.user === uid && bot?.active && (
                      <div className="flex items-center">
                        <div className="text-xs text-primary-50 px-4">
                          Actions
                        </div>
                        <IonButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setActiveAction("resetPortfolio");
                            presentConfirm();
                          }}
                        >
                          <IonIcon icon={refreshOutline} slot="icon-only" />
                        </IonButton>
                        <IonButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setActiveAction("clearDust");
                            presentConfirm();
                          }}
                        >
                          <IonIcon
                            icon={removeCircleOutline}
                            slot="icon-only"
                          />
                        </IonButton>
                      </div>
                    )}
                  </div>
                  {bot.mode !== "backtest" && (
                    <GradientLineChart data={formattedData} />
                  )}

                  <CoinList botId={botId} />
                </div>
              )}
              {activeSegment === "options" && (
                <>
                  <div className="flex justify-between items-center px-4">
                    <div className="text-2xl text-white font-semibold flex items-center">
                      Buy Options
                      <HelpButton id="buy_options" />
                    </div>
                  </div>
                  <CoinList botId={botId} options={true} />
                </>
              )}
              {activeSegment === "trades" && (
                <>
                  <div className="flex justify-between items-center px-4">
                    <div className="text-2xl text-white font-semibold flex items-center">
                      Trades
                      <HelpButton id="bot_trades" />
                    </div>
                  </div>
                  <TradeList botId={botId} />
                </>
              )}
              {activeSegment === "config" && (
                <>
                  <div className="flex justify-between items-center px-4">
                    <div className="text-2xl text-white font-semibold flex items-center">
                      Bot Config
                      <HelpButton id="bot_config" />
                    </div>
                  </div>
                  <ConfigSummary bot={bot} />
                </>
              )}
              <div className="ion-text-center p-4">
                <IonButton
                  className="h-16 text-base"
                  color="primary"
                  expand="block"
                  onClick={() => {
                    console.log("enter bot");
                  }}
                >
                  Enter Lobby
                  <IonIcon slot="end" icon={addOutline} />
                </IonButton>
              </div>
            </div>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default Bot;
